import { useLayoutEffect, useState, type RefObject } from 'react'

/** Viewport-relative `position: fixed` coordinate for an anchored menu. */
export interface AnchoredMenuPosition {
  top: number
  left: number
}

/** Minimum gap (px) kept between the menu and the viewport edge it's clamped against. */
const VIEWPORT_MARGIN = 8

/**
 * Computes a `position: fixed` (viewport-relative) coordinate for a dropdown/menu anchored
 * below a trigger element, tracking it as the page scrolls or resizes while open.
 *
 * Menus that use plain `position: absolute` inside a scrollable ancestor (e.g. a table wrapped
 * in `overflow-x-auto`) get visually clipped by that ancestor — the panel is invisible or cut off
 * even though it's "open". Pairing this hook with a `createPortal(..., document.body)` render
 * moves the menu outside any such clipping context entirely, so it's always fully visible.
 *
 * Also flips the menu **above** the trigger (and clamps it within the viewport) when there isn't
 * enough room below for `estimatedHeight` — e.g. a trigger near the bottom of a mobile viewport
 * would otherwise render its options partly or fully off-screen, with no way to scroll a
 * `position: fixed` element into view, making them unreachable/unclickable.
 *
 * @param align Horizontal alignment of the menu relative to the trigger's edge.
 * @param estimatedHeight Approximate rendered height (px) of the menu, used only to decide
 *   whether it fits below the trigger. Callers with small, roughly-fixed-height menus (a handful
 *   of options) can pass a conservative estimate — it only needs to be in the right ballpark,
 *   since being anchored `fixed` to the trigger is what actually keeps it visible either way.
 */
export function useAnchoredMenuPosition(
  anchorRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
  align: 'left' | 'right' = 'left',
  estimatedHeight = 160
): AnchoredMenuPosition | null {
  const [position, setPosition] = useState<AnchoredMenuPosition | null>(null)

  useLayoutEffect(() => {
    if (!isOpen) return

    function updatePosition() {
      const el = anchorRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const spaceBelow = window.innerHeight - rect.bottom
      const fitsBelow = spaceBelow >= estimatedHeight + VIEWPORT_MARGIN

      const top = fitsBelow
        ? rect.bottom + 6
        : // Flip above the trigger; clamp so a menu taller than the remaining space above still
          // stays fully on-screen instead of running off the top edge.
          Math.max(VIEWPORT_MARGIN, rect.top - 6 - estimatedHeight)

      setPosition({
        top,
        left: align === 'right' ? rect.right : rect.left,
      })
    }

    updatePosition()
    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)
    return () => {
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [isOpen, anchorRef, align, estimatedHeight])

  // Derive the closed state instead of resetting it via a synchronous setState in the effect
  // above — stale coordinates from the last open render never leak out while closed.
  return isOpen ? position : null
}
